import { motion } from 'framer-motion';
import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppWidget from '../../components/feature/WhatsAppWidget';
import CookieConsent from '../../components/feature/CookieConsent';
import { updateSEO, getBaseUrl } from '../../utils/seo';

const AboutPage = () => {
  useEffect(() => {
    const baseUrl = getBaseUrl();
    
    updateSEO({
      title: 'Hakkımızda | Bursa Sulama Sistemleri - 12 Yıllık Deneyim',
      description: '2013\'ten beri Bursa\'da sulama sistemleri alanında hizmet veriyoruz. 8 kişilik uzman ekibimiz, 500+ başarılı proje deneyimi ile Nilüfer, Görükle ve çevre bölgelerde profesyonel sulama çözümleri sunmaktadır.',
      keywords: 'bursa sulama sistemleri hakkında, nilüfer sulama firması, görükle sulama şirketi, profesyonel sulama ekibi, deneyimli sulama uzmanları',
      canonical: `${baseUrl}/hakkimizda`,
      ogImage: 'https://static.readdy.ai/image/f7a5d02425e0675ec2bf06c3a77d9570/53f31445c094a5585e3a104c7765fed6.jpeg',
      schema: {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: 'Hakkımızda - Bursa Sulama Sistemleri',
        description: '2013\'ten beri Bursa\'da sulama sistemleri alanında hizmet veriyoruz. 8 kişilik uzman ekibimiz ile profesyonel çözümler sunuyoruz.',
        url: `${baseUrl}/hakkimizda`,
        mainEntity: {
          '@type': 'Organization',
          name: 'Bursa Sulama Sistemleri',
          foundingDate: '2013',
          numberOfEmployees: 8,
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Atatürk Caddesi No:72, Hasan Boztürk Apartmanı',
            addressLocality: 'Görükle, Nilüfer',
            addressRegion: 'Bursa',
            addressCountry: 'TR'
          },
          areaServed: ['Nilüfer', 'Görükle', 'Osmangazi', 'Yıldırım', 'Mudanya', 'Gemlik', 'İnegöl', 'Mustafakemalpaşa']
        }
      }
    });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-20 bg-gradient-to-br from-[#14B8A6]/10 to-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-3xl md:text-5xl lg:text-7xl font-bold text-[#0A1628] mb-4 md:mb-6">
              Hakkımızda
            </h1>
            <p className="text-base md:text-xl lg:text-2xl text-gray-600 leading-relaxed px-4">
              12 yıllık deneyimimizle Bursa'nın sulama sistemleri alanında
              güvenilir çözüm ortağınız
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-20 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 items-center mb-12 md:mb-20">
            <motion.div
              initial={{ opacity: 1, x: 0 }}
            >
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://static.readdy.ai/image/f7a5d02425e0675ec2bf06c3a77d9570/53f31445c094a5585e3a104c7765fed6.jpeg"
                  alt="Bursa Sulama Sistemleri"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 1, x: 0 }}
            >
              <div className="flex items-center gap-2 mb-4 md:mb-6">
                <div className="w-2 h-2 bg-[#14B8A6] rounded-full"></div>
                <span className="text-xs md:text-sm text-[#0A1628] font-medium uppercase tracking-wider">
                  Biz Kimiz
                </span>
              </div>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4 md:mb-6 leading-tight">
                Bursa'nın Sulama Sistemleri Uzmanı
              </h2>
              <div className="space-y-3 md:space-y-4 text-sm md:text-lg text-gray-600 leading-relaxed">
                <p>
                  2013 yılından bu yana Bursa'da faaliyet gösteren firmamız,
                  sulama sistemleri alanında 12 yıllık deneyimi ile bölgenin en
                  güvenilir çözüm ortaklarından biri haline gelmiştir.
                </p>
                <p>
                  Görükle, Nilüfer merkezli olarak hizmet veren şirketimiz, 8
                  kişilik uzman ekibi ile konut bahçelerinden ticari alanlara,
                  tarımsal arazilerden spor tesislerine kadar geniş bir
                  yelpazede profesyonel sulama çözümleri sunmaktadır.
                </p>
                <p>
                  Modern teknoloji ve kaliteli malzeme kullanımını ilke edinen
                  firmamız, müşteri memnuniyetini her zaman ön planda
                  tutmaktadır. 500'den fazla başarılı proje ile kazandığımız
                  deneyim ve güven, bizi sektörde öne çıkaran en önemli
                  değerlerimizdir.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Values Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 mb-12 md:mb-20">
            {[
              {
                icon: 'ri-shield-check-line',
                title: 'Güvenilirlik',
                description:
                  '12 yıllık sektör deneyimi ve 500+ başarılı proje ile kanıtlanmış güvenilirlik',
              },
              {
                icon: 'ri-award-line',
                title: 'Kalite',
                description:
                  'En kaliteli malzemeler ve modern teknoloji ile uzun ömürlü çözümler',
              },
              {
                icon: 'ri-customer-service-2-line',
                title: 'Müşteri Memnuniyeti',
                description:
                  '7/24 destek hizmeti ve müşteri odaklı çözüm yaklaşımı',
              },
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 1, y: 0 }}
                className="bg-[#F8F6F3] rounded-2xl p-6 md:p-8 hover:shadow-xl transition-shadow"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 bg-[#14B8A6] rounded-2xl flex items-center justify-center mb-4 md:mb-6">
                  <i className={`${value.icon} text-3xl md:text-4xl text-white`}></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-[#0A1628] mb-2 md:mb-3">
                  {value.title}
                </h3>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Team Section */}
          <motion.div
            initial={{ opacity: 1, y: 0 }}
            className="bg-[#0A1628] rounded-3xl p-6 md:p-12 lg:p-16 text-white"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6">
                  Uzman Ekibimiz
                </h2>
                <p className="text-base md:text-xl text-gray-300 leading-relaxed mb-6 md:mb-8">
                  8 kişilik profesyonel ekibimiz, alanında uzman teknik
                  personel ve deneyimli montaj ekiplerinden oluşmaktadır. Her
                  proje için en uygun çözümü sunmak ve kusursuz uygulama
                  gerçekleştirmek için sürekli eğitim ve gelişim içindeyiz.
                </p>
                <div className="grid grid-cols-2 gap-6 md:gap-8">
                  <div>
                    <div className="text-3xl md:text-5xl font-bold text-[#14B8A6] mb-2">
                      8
                    </div>
                    <div className="text-sm md:text-base text-gray-400">Uzman Personel</div>
                  </div>
                  <div>
                    <div className="text-3xl md:text-5xl font-bold text-[#14B8A6] mb-2">
                      7/24
                    </div>
                    <div className="text-sm md:text-base text-gray-400">Destek Hizmeti</div>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/f7a5d02425e0675ec2bf06c3a77d9570/18e4d307803a4b8dddbfd9bbc7c85aac.jpeg"
                  alt="Ekibimiz"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </motion.div>

          {/* Service Areas */}
          <motion.div
            initial={{ opacity: 1, y: 0 }}
            className="mt-12 md:mt-20 text-center"
          >
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4 md:mb-6 px-4">
              Hizmet Bölgelerimiz
            </h2>
            <p className="text-base md:text-xl text-gray-600 mb-8 md:mb-12 max-w-3xl mx-auto px-4">
              Bursa'nın tüm ilçelerinde hizmet vermekteyiz. Özellikle Nilüfer,
              Görükle ve çevre bölgelerde yoğun olarak faaliyet göstermekteyiz.
            </p>
            <div className="flex flex-wrap justify-center gap-3 md:gap-4 px-4">
              {[
                'Nilüfer',
                'Görükle',
                'Osmangazi',
                'Yıldırım',
                'Mudanya',
                'Gemlik',
                'İnegöl',
                'Mustafakemalpaşa',
              ].map((area, index) => (
                <span
                  key={index}
                  className="px-4 md:px-6 py-2 md:py-3 bg-[#14B8A6]/10 text-[#0A1628] rounded-full font-medium text-sm md:text-base"
                >
                  {area}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-[#14B8A6] to-[#0F9B8E]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6 px-4">
              Profesyonel Sulama Çözümleri İçin Bize Ulaşın
            </h2>
            <p className="text-base md:text-xl text-white/90 mb-6 md:mb-8 max-w-2xl mx-auto px-4">
              Ücretsiz keşif ve danışmanlık hizmeti için hemen iletişime geçin
            </p>
            <a
              href="tel:+905071344668"
              className="inline-flex items-center gap-3 bg-white text-[#0A1628] px-8 md:px-10 py-4 md:py-5 rounded-full text-lg md:text-xl font-semibold hover:scale-105 transition-all duration-300 shadow-2xl whitespace-nowrap"
            >
              <i className="ri-phone-fill text-xl md:text-2xl"></i>
              <span>0507 134 46 68</span>
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
      <WhatsAppWidget />
      <CookieConsent />
    </div>
  );
};

export default AboutPage;
